import { FC, ReactNode, useId } from 'react'
import { Skeleton } from '../Skeleton'
import { Button } from 'components/buttons'
import { Tooltip } from '@lib/tooltip'
import { twMerge } from '@lib/tailwind-merge'
import { Spinner } from 'components/loaders'
import {
  faCloudUpload,
  faLock,
  faCalendarPen,
} from '@fortawesome/pro-regular-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import {
  TimeCardsContextTimeCard,
  useTimeCardsContext,
} from '@features/time-logging/providers/TimeCardsContextProvider'

interface Props {
  timeCard: TimeCardsContextTimeCard
  className: string
}

const Status = ({
  icon,
  children,
}: {
  icon: IconDefinition
  children: ReactNode
}) => {
  return (
    <div className="flex items-center text-sm gap-2 text-neutral-900">
      <FontAwesomeIcon icon={icon} />
      {children}
    </div>
  )
}

const SubmitButton: FC<{ timeCard: TimeCardsContextTimeCard }> = ({
  timeCard,
}) => {
  const { t } = useTranslation()
  const { isLoading: timeCardsAreLoading } = useTimeCardsContext()
  const tooltipId = useId()
  const { submissionBlockedReason } = timeCard

  return (
    /**
     * A disabled <button> emits none of the pointer events react-tooltip
     * listens for, so the tooltip explaining why submission is blocked has to
     * be anchored to this wrapper rather than to the button itself.
     */
    <div className="flex grow" data-tooltip-id={tooltipId}>
      <Button
        disabled={!timeCard.isValid || timeCardsAreLoading}
        className="text-sm grow px-2.5"
        onClick={() => timeCard.submit()}
        autoTextSize={true}
        maxAutoFontSizePx={14}
      >
        {timeCardsAreLoading ? (
          <Spinner className="text-white" />
        ) : (
          t('features.timeLogging.timeCardActions.submit')
        )}
      </Button>

      {submissionBlockedReason && (
        <Tooltip id={tooltipId} place="bottom" className="max-w-xs">
          {t(
            `features.timeLogging.submissionBlockedReasons.${submissionBlockedReason}`,
          )}
        </Tooltip>
      )}
    </div>
  )
}

const FooterContent: FC<{ timeCard: TimeCardsContextTimeCard }> = ({
  timeCard,
}) => {
  const { t } = useTranslation()
  const { isLoading: timeCardsAreLoading } = useTimeCardsContext()

  // Submitted Time Cards can exist in the future for admin-managed groups
  if (timeCard.isInFuture && !timeCard.submitted) return <Skeleton />

  if (timeCard.correction) {
    if (timeCard.correctionState === 'draft') {
      return (
        <Button
          disabled={!timeCard.isValid || timeCardsAreLoading}
          className="text-sm grow px-2.5"
          onClick={() => timeCard.saveCorrection()}
          autoTextSize={true}
          maxAutoFontSizePx={14}
        >
          {timeCardsAreLoading ? (
            <Spinner className="text-white" />
          ) : (
            t('common.save')
          )}
        </Button>
      )
    }

    return (
      <Status icon={faCalendarPen}>
        {t('features.timeLogging.corrected')}
      </Status>
    )
  }

  if (timeCard.sentToPayrollAt) {
    return (
      <Status icon={faCloudUpload}>
        {t('features.timeLogging.timeCardStates.sent')}
      </Status>
    )
  }

  if (timeCard.submitted) {
    // The existence of a DayLock implies an Unsubmission Lock
    if (timeCard.lock) {
      return (
        <Status icon={faLock}>
          {t('features.timeLogging.timeCardStates.locked')}
        </Status>
      )
    }

    return (
      // Unsubmit button is disabled on future Time Cards (only possible for admin-managed)
      <Button
        className="text-sm bg-white border-[1.5px] grow w-full px-2.5"
        disabled={timeCardsAreLoading || timeCard.isInFuture}
        onClick={() => timeCard.unsubmit()}
        variant="outlined"
        autoTextSize={true}
        maxAutoFontSizePx={14}
      >
        {timeCardsAreLoading ? (
          <Spinner className="text-neutral-900" />
        ) : (
          t('features.timeLogging.timeCardActions.unsubmit')
        )}
      </Button>
    )
  }

  return <SubmitButton timeCard={timeCard} />
}

export const Footer: FC<Props> = ({ timeCard, className }) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-center h-12 px-2.5 xxl:px-5',
        className,
      )}
    >
      <FooterContent timeCard={timeCard} />
    </div>
  )
}
